import _ from 'lodash'
import moment from 'moment'

export const transform = (item) => {
  const primaryOffice = _.find(item.offices, { primary: true })
  const isPhaseTwoPending =
    item.pendingTasks?.some((task) => task.type === 'LO_PHASE_TWO' && task.status === 'PENDING') || false
  const isLOALicensedPending =
    item.pendingTasks?.some((task) => task.type === 'LOA_LICENSE' && task.status === 'PENDING') || false
  const user = {
    ...item,
    id: item.id,
    name: item.name,
    firstName: item.firstName,
    lastName: item.lastName,
    jobTitle: item.jobTitle,
    birthMonth: item.birthMonth,
    birthDay: item.birthDay,
    status: item.status,
    email: item.email,
    phoneMobile: item.phoneMobile,
    phoneMobileExt: item.phoneMobileExt,
    phoneFax: item.phoneFax,
    phoneFaxExt: item.phoneFaxExt,
    nmlsNumber: item.nmlsNumber,
    nmlsFirstName: item.nmlsFirstName,
    nmlsMiddleName: item.nmlsMiddleName,
    nmlsLastName: item.nmlsLastName,
    nmlsPhone: item.nmlsPhone,
    nmlsPhoneExt: item.nmlsPhoneExt,
    shortDescription: item.shortDescription,
    domain: item.domain,
    photoUrl: item.photoUrl,
    primary: item.primary,
    primaryOwner: item.primaryOwner,
    mainContact: item.mainContact,
    awards: item.awards,
    locationDetails: item.locationDetails,
    licenses: item.licenses,
    deactivateDate: item.deactivateDate,
    trainings: item.trainings?.map((training) => ({
      ...training,
      utcDate: training.date ? moment(training.date, 'LL').utc().format() : '',
    })),
    mortgageApplicLink: item.mortgageApplicLink,
    keywords: item.keywords,
    pendingTasks: item.pendingTasks,
    emailNotifications: item.emailNotifications,
    automatedEmails: item.automatedEmails,
    personnelManagement: item.personnelManagement,
    primaryOffice,
    birthDayFormatted:
      item.birthDay && item.birthMonth
        ? `${_.padStart(item.birthMonth, 2, '0')}/${_.padStart(item.birthDay, 2, '0')}`
        : null,
    originalLicenses: _.cloneDeep(item.licenses),
    sfid: null,
    loPhase: parseInt(item.loPhase),
    statusOrder: statusOrder[item.status],
    isHQUser: item.groups.some((group) => group.accountTypeLabel === 'Motto Corporate'),
    groupLabels: _(item.groups)
      .orderBy('priority', 'desc')
      .map((group) => {
        if (group.label === 'RecordOnly') {
          return 'Record Only'
        }

        if (group.label === 'Loan Processor') {
          return 'In-house Processor'
        }

        if (group.label === 'Loan Originator' && parseInt(item.loPhase) === 2) {
          return 'MLO Phase 2'
        }

        if (group.label === 'Loan Originator' && isPhaseTwoPending) {
          return 'MLO Phase 2 (pending)'
        }

        if (group.label === 'LOA - Unlicensed' && isLOALicensedPending) {
          return 'LOA - Licensed (pending)'
        }

        if (group.label === 'Loan Originator') {
          return 'MLO Phase 1'
        }

        return group.label
      })
      .uniq()
      .value(),
    groupPersonnelLabels: _(item.groups)
      .orderBy('priority', 'desc')
      .map((group) => {
        if (group.label === 'Loan Processor') {
          return 'In-house Processor'
        }

        if (group.label === 'Loan Originator' && parseInt(item.loPhase) === 2) {
          return 'Loan Originator Phase 2'
        }

        if (group.label === 'Loan Originator') {
          return 'Loan Originator Phase 1'
        }

        return group.label
      })
      .uniq()
      .value(),
    loPhaseChangedAt: item.loPhaseChangedAt,
    webAdminChangedAts: item.webAdminIdChangedAts || [],
    activationDate: item.activationDate,
    isWebAdmin: item.isWebAdmin,
    hasCoreTechTraining: item.trainings?.some((training) => training?.name === 'Core Tech Onboarding'),
    isBrokerOwner: item.groups.some((group) => group.label === 'Broker Owner'),
    isLoPhaseTwo: item.groups.some((group) => group.label === 'Loan Originator') && parseInt(item.loPhase) === 2,
    isThirdParty: item.groups.some((group) => group.label === 'Third-party Processor'),
    isLoanProcessor: item.groups.some((group) => group.label === 'Loan Processor'),
    isLoaLicensed: item.groups.some((group) => group.label === 'LOA - Licensed'),
    isLicensed: !_.isEmpty(item.licenses),
    isAccessOnly: item.groups.some((group) => group.accountTypeLabel === 'Access Only'),
    isRecordOnly: item.groups.some((group) => group.label === 'RecordOnly'),
    isLoPhaseTwoReviewPending: isPhaseTwoPending,
    isLoaLicensedReviewPending: isLOALicensedPending,
    isDeactivationPending:
      item.pendingTasks?.some(
        (task) => (task.type === 'DEACTIVATION' || task.type === 'ACCESSIBLE_TERMINATION') && task.status === 'PENDING'
      ) || false,
    systemOfRecord: _.find(item.offices, { primary: true })?.systemOfRecord,
    isLicenseChangePending:
      item.pendingTasks?.some((task) => task.type === 'STATE_LICENSE' && task.status === 'PENDING') || false,
    pendingLicenses:
      item.pendingTasks
        ?.filter((task) => task.type === 'STATE_LICENSE' && task.status === 'PENDING')
        .map((x) => x.additionalData?.requestedLicense) || [],
    managedPhaseTwoLoanOriginators: transformManagedPhaseTwoLoanOriginators(item.managedPhaseTwoLoanOriginators),
  }

  return {
    ...user,
    isInactive:
      user.status === 'inactive' ||
      user.status === 'accessible_termination' ||
      user.groupLabels.includes('Record Only'),
    isAnythingPending:
      user.isLoPhaseTwoReviewPending ||
      user.isDeactivationPending ||
      user.isLicenseChangePending ||
      user.isLoaLicensedReviewPending,
    shouldReviewDeactivation: user.isLoPhaseTwo || user.isLoanProcessor,
  }
}

const statusOrder = {
  active: 0,
  email_sent: 1,
  accessible_termination: 2,
  inactive: 3,
  created: 4,
}

export const transformMany = (items = []) => {
  return items.map((item) => transform(item))
}

export const transformManagedPhaseTwoLoanOriginators = (items) => {
  if (!items || !items.length) {
    return []
  }

  return items.map((item) => ({
    id: item.id,
    name: item.name,
  }))
}
